/**
 * Choosing which physical camera to open.
 *
 * `facingMode: 'environment'` is not good enough on iOS. Since 16.4 it
 * frequently binds the ultra-wide lens, and on iOS 18 the system switches
 * between wide / ultra-wide / telephoto mid-session even with `exact`. The old
 * app used exactly that constraint, which is a large part of why codes needed
 * the camera manoeuvred into a sweet spot.
 *
 * Since iOS 16.3 `enumerateDevices()` exposes every rear lens individually, so
 * we pick one by explicit `deviceId` instead.
 *
 * The catch: `deviceId` values are rotated per origin and per session, so they
 * cannot be persisted. What *is* stable is the label ("Back Camera", "Back
 * Ultra Wide Camera"), so a saved choice is stored as `{label, ordinal}` and
 * re-resolved at every launch. The ordinal disambiguates the duplicate labels
 * Android reports.
 *
 * There is no single "best" lens: the ultra-wide has no minimum focus distance
 * and is often better for a small code held close, while the main wide is
 * sharper further out. Rather than guess on the user's behalf, the app defaults
 * to the main wide and offers a picker.
 */

export interface CameraOption {
  deviceId: string
  label: string
  /** Index among cameras sharing this label — Android reports duplicates. */
  ordinal: number
  facing: 'front' | 'back' | 'unknown'
  /** Best guess at the lens type, for labelling the picker. */
  lens: 'wide' | 'ultra-wide' | 'telephoto' | 'unknown'
}

/** Persisted form of a user's choice. Deliberately not a deviceId. */
export interface LensChoice {
  label: string
  ordinal: number
}

const FRONT = /front|face|user|selfie/i
const BACK = /back|rear|environment|world/i
const ULTRA = /ultra.?wide|0\.5|wide.?angle.?\(ultra/i
const TELE = /tele|zoom|3x|5x/i

function classifyFacing(label: string): CameraOption['facing'] {
  if (FRONT.test(label)) return 'front'
  if (BACK.test(label)) return 'back'
  return 'unknown'
}

function classifyLens(label: string): CameraOption['lens'] {
  // Order matters: "Back Ultra Wide Camera" contains "Wide" too.
  if (ULTRA.test(label)) return 'ultra-wide'
  if (TELE.test(label)) return 'telephoto'
  if (/wide|back|rear/i.test(label)) return 'wide'
  return 'unknown'
}

/**
 * Turn raw `MediaDeviceInfo`s into camera options.
 *
 * Labels are empty until camera permission has been granted at least once, so
 * this must be called *after* the first successful getUserMedia — otherwise
 * every device looks identical and the picker is useless.
 */
export function toCameraOptions(devices: readonly MediaDeviceInfo[]): CameraOption[] {
  const seen = new Map<string, number>()

  return devices
    .filter((d) => d.kind === 'videoinput')
    .map((d) => {
      const label = d.label.trim()
      const ordinal = seen.get(label) ?? 0
      seen.set(label, ordinal + 1)
      return {
        deviceId: d.deviceId,
        label,
        ordinal,
        facing: classifyFacing(label),
        lens: classifyLens(label),
      }
    })
}

export function rearCameras(cameras: readonly CameraOption[]): CameraOption[] {
  const back = cameras.filter((c) => c.facing === 'back')
  // If nothing is identifiable as rear-facing (labels can be blank or
  // non-English), fall back to everything rather than showing an empty picker.
  return back.length > 0 ? back : [...cameras]
}

/**
 * Default lens: the main wide rear camera.
 *
 * Chosen over the ultra-wide because it is the sharper sensor and the one users
 * expect "the camera" to mean. On an iPhone the plain "Back Camera" entry is
 * the main wide lens; the composite "Back Dual/Triple Wide Camera" entries are
 * the ones that let iOS switch lenses underneath us, so they are ranked below
 * a single explicit lens.
 */
export function pickDefaultCamera(cameras: readonly CameraOption[]): CameraOption | null {
  const candidates = rearCameras(cameras)
  if (candidates.length === 0) return null

  const score = (c: CameraOption): number => {
    if (/^back camera$/i.test(c.label)) return 0 // iOS main wide — ideal
    if (c.lens === 'wide' && !/dual|triple/i.test(c.label)) return 1
    if (c.lens === 'wide') return 2 // composite: iOS may switch lenses on us
    if (c.lens === 'unknown') return 3
    if (c.lens === 'telephoto') return 4
    return 5 // ultra-wide: great up close, poor at any distance
  }

  return [...candidates].sort((a, b) => score(a) - score(b) || a.ordinal - b.ordinal)[0] ?? null
}

/**
 * Re-resolve a saved choice against the cameras present right now.
 *
 * Returns the default when the saved lens has gone — which happens routinely,
 * not just in theory: plug in a webcam, or open the app on a different phone,
 * and the labels change. Silently falling back beats failing to open a camera.
 */
export function resolveChoice(
  cameras: readonly CameraOption[],
  choice: LensChoice | null,
): CameraOption | null {
  if (choice !== null) {
    const exact = cameras.find((c) => c.label === choice.label && c.ordinal === choice.ordinal)
    if (exact !== undefined) return exact

    // Same label, different ordinal — the device order shifted. Still the lens
    // the user asked for, so prefer it over falling all the way back.
    const byLabel = cameras.find((c) => c.label === choice.label)
    if (byLabel !== undefined) return byLabel
  }
  return pickDefaultCamera(cameras)
}

export function toChoice(camera: CameraOption): LensChoice {
  return { label: camera.label, ordinal: camera.ordinal }
}

/** Short human label for the picker: "Main", "Ultra wide", "Telephoto". */
export function describeLens(camera: CameraOption): string {
  switch (camera.lens) {
    case 'ultra-wide':
      return 'Ultra wide'
    case 'telephoto':
      return 'Telephoto'
    case 'wide':
      return /dual|triple/i.test(camera.label) ? 'Auto (switches lens)' : 'Main'
    default:
      return camera.label === '' ? 'Camera' : camera.label
  }
}

const STORAGE_KEY = 'scanflow.lens'

export function loadLensChoice(): LensChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as LensChoice).label === 'string' &&
      typeof (parsed as LensChoice).ordinal === 'number'
    ) {
      return parsed as LensChoice
    }
  } catch {
    /* corrupt or unavailable storage must not stop the camera opening */
  }
  return null
}

export function saveLensChoice(choice: LensChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice))
  } catch {
    /* private mode / quota — the choice just won't persist */
  }
}
